import React, { useEffect, useRef } from 'react';
import '../styles/PacmanBoard.css';

// Game constants
const CELL_SIZE = 20;
const BOARD_WIDTH = 28;
const BOARD_HEIGHT = 31;

function PacmanBoard({ gameState, playerId }) {
    const canvasRef = useRef(null);

    // Find the current player
    const currentPlayer = gameState.players.find(player => player.id === playerId);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Draw the game
        drawGame(ctx, gameState, playerId);

        // Animation frame for smoother rendering
        let animationFrameId;

        const render = () => {
            drawGame(ctx, gameState, playerId);
            animationFrameId = window.requestAnimationFrame(render);
        };

        render();

        return () => {
            window.cancelAnimationFrame(animationFrameId);
        };
    }, [gameState, playerId]);

    const drawGame = (ctx, gameState, playerId) => {
        // Clear the canvas
        ctx.fillStyle = 'black';
        ctx.fillRect(0, 0, BOARD_WIDTH * CELL_SIZE, BOARD_HEIGHT * CELL_SIZE);

        // Draw the board
        for (let y = 0; y < gameState.board.length; y++) {
            for (let x = 0; x < gameState.board[y].length; x++) {
                const cell = gameState.board[y][x];

                switch (cell) {
                    case 'wall':
                        ctx.fillStyle = 'blue';
                        ctx.fillRect(x * CELL_SIZE, y * CELL_SIZE, CELL_SIZE, CELL_SIZE);
                        break;
                    case 'pellet':
                        ctx.fillStyle = 'white';
                        ctx.beginPath();
                        ctx.arc(
                            x * CELL_SIZE + CELL_SIZE / 2,
                            y * CELL_SIZE + CELL_SIZE / 2,
                            CELL_SIZE / 10,
                            0,
                            Math.PI * 2
                        );
                        ctx.fill();
                        break;
                    case 'power-pellet':
                        ctx.fillStyle = 'white';
                        ctx.beginPath();
                        ctx.arc(
                            x * CELL_SIZE + CELL_SIZE / 2,
                            y * CELL_SIZE + CELL_SIZE / 2,
                            CELL_SIZE / 4,
                            0,
                            Math.PI * 2
                        );
                        ctx.fill();
                        break;
                    default:
                        break;
                }
            }
        }

        // Draw the players
        gameState.players.forEach(player => {
            if (!player.isAlive) return;

            const { x, y } = player.position;

            // Draw Pac-Man body
            ctx.fillStyle = player.color;
            ctx.beginPath();

            // Calculate mouth angle based on direction
            let startAngle = 0.2 * Math.PI;
            let endAngle = 1.8 * Math.PI;

            switch (player.direction) {
                case 'right':
                    startAngle = 0.2 * Math.PI;
                    endAngle = 1.8 * Math.PI;
                    break;
                case 'down':
                    startAngle = 0.7 * Math.PI;
                    endAngle = 2.3 * Math.PI;
                    break;
                case 'left':
                    startAngle = 1.2 * Math.PI;
                    endAngle = 0.8 * Math.PI;
                    break;
                case 'up':
                    startAngle = 1.7 * Math.PI;
                    endAngle = 1.3 * Math.PI;
                    break;
                default:
                    break;
            }

            ctx.arc(
                x * CELL_SIZE + CELL_SIZE / 2,
                y * CELL_SIZE + CELL_SIZE / 2,
                CELL_SIZE / 2,
                startAngle,
                endAngle
            );
            ctx.lineTo(
                x * CELL_SIZE + CELL_SIZE / 2,
                y * CELL_SIZE + CELL_SIZE / 2
            );
            ctx.fill();

            // Highlight current player
            if (player.id === playerId) {
                ctx.strokeStyle = 'white';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.arc(
                    x * CELL_SIZE + CELL_SIZE / 2,
                    y * CELL_SIZE + CELL_SIZE / 2,
                    CELL_SIZE / 2 + 2,
                    0,
                    Math.PI * 2
                );
                ctx.stroke();
            }
        });

        // Draw the ghosts
        gameState.ghosts.forEach(ghost => {
            const { x, y } = ghost.position;

            // Draw ghost body
            ctx.fillStyle = ghost.isVulnerable ? 'blue' : ghost.color;

            // Ghost head (semi-circle)
            ctx.beginPath();
            ctx.arc(
                x * CELL_SIZE + CELL_SIZE / 2,
                y * CELL_SIZE + CELL_SIZE / 2 - CELL_SIZE / 4,
                CELL_SIZE / 2,
                Math.PI,
                0
            );

            // Ghost body (rectangle with wavy bottom)
            ctx.rect(
                x * CELL_SIZE,
                y * CELL_SIZE + CELL_SIZE / 4,
                CELL_SIZE,
                CELL_SIZE / 2
            );

            ctx.fill();

            // Draw ghost eyes
            ctx.fillStyle = 'white';
            ctx.beginPath();
            ctx.arc(
                x * CELL_SIZE + CELL_SIZE / 3,
                y * CELL_SIZE + CELL_SIZE / 3,
                CELL_SIZE / 6,
                0,
                Math.PI * 2
            );
            ctx.arc(
                x * CELL_SIZE + CELL_SIZE * 2/3,
                y * CELL_SIZE + CELL_SIZE / 3,
                CELL_SIZE / 6,
                0,
                Math.PI * 2
            );
            ctx.fill();

            // Draw ghost pupils
            ctx.fillStyle = 'black';

            // Adjust pupil position based on direction
            let pupilOffsetX = 0;
            let pupilOffsetY = 0;

            switch (ghost.direction) {
                case 'up':
                    pupilOffsetY = -CELL_SIZE / 12;
                    break;
                case 'down':
                    pupilOffsetY = CELL_SIZE / 12;
                    break;
                case 'left':
                    pupilOffsetX = -CELL_SIZE / 12;
                    break;
                case 'right':
                    pupilOffsetX = CELL_SIZE / 12;
                    break;
                default:
                    break;
            }

            ctx.beginPath();
            ctx.arc(
                x * CELL_SIZE + CELL_SIZE / 3 + pupilOffsetX,
                y * CELL_SIZE + CELL_SIZE / 3 + pupilOffsetY,
                CELL_SIZE / 12,
                0,
                Math.PI * 2
            );
            ctx.arc(
                x * CELL_SIZE + CELL_SIZE * 2/3 + pupilOffsetX,
                y * CELL_SIZE + CELL_SIZE / 3 + pupilOffsetY,
                CELL_SIZE / 12,
                0,
                Math.PI * 2
            );
            ctx.fill();
        });
    };

    return (
        <div className="pacman-board">
            <canvas
                ref={canvasRef}
                width={BOARD_WIDTH * CELL_SIZE}
                height={BOARD_HEIGHT * CELL_SIZE}
                className="game-canvas"
            />

            {currentPlayer && (
                <div className="player-info">
                    You: {currentPlayer.name} - Score: {currentPlayer.score}
                    {!currentPlayer.isAlive && " (DEAD)"}
                </div>
            )}
        </div>
    );
}

export default PacmanBoard;
